import { useAuth } from "@dl/web-core-js/auth";
import { Button, cn, Dialog } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Delete, Minus, Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../api/manufacturing";

/** The station kiosk's touch parts, shared by its step view and its cut job view. */

export interface ScrapReasonDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  is_active: boolean;
}

export function Figure({ label, value, unit, strong, tone }: { label: string; value: number; unit?: string; strong?: boolean; tone?: "warn" | "risk" }) {
  return (
    <div className="rounded-lg border border-line px-3 py-2">
      <div className="min-h-[2.5em] text-[13px] leading-tight text-ink-muted">{label}</div>
      <div className={cn("tabular-nums", strong ? "text-[36px] font-bold" : "text-[28px] font-semibold", tone === "warn" && "text-warn", tone === "risk" && "text-risk")}>
        {formatNumber(value)}
      </div>
      {unit && <div className="text-[12px] text-ink-faint">{unit}</div>}
    </div>
  );
}

export function BigButton({
  icon,
  label,
  onClick,
  disabled,
  primary,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex min-h-16 items-center justify-center gap-2 rounded-xl border-2 px-3 text-[20px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        primary ? "border-accent bg-accent text-ink-on-accent hover:opacity-90" : "border-line bg-surface hover:bg-surface-sunk",
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="min-w-0 leading-tight">{label}</span>
    </button>
  );
}

/** A number typed on a big keypad: the scanner's keyboard is not the operator's. */
export function QuantityDialog({
  title,
  hint,
  initial,
  max,
  onClose,
  onSubmit,
}: {
  title: string;
  hint?: string;
  initial: number;
  /** A Max button sets this: the full quantity left. */
  max?: number;
  onClose: () => void;
  onSubmit: (value: number) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(String(initial));
  const value = Number(text === "" ? 0 : text);
  // Never below zero: a report made by mistake is undone, not reported negative.
  const add = (delta: number) => setText(String(Math.max(0, value + delta)));
  const past = max != null && value > max;
  const press = (digit: string) => setText((current) => (current === "0" ? digit : current + digit));
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={hint}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!Number.isFinite(value)} onClick={() => onSubmit(value)}>
            {t("kiosk.ok", "OK")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className={cn("rounded-lg border-2 px-4 py-3 text-right text-[44px] font-bold tabular-nums", past ? "border-warn text-warn" : "border-accent")}>
          {text === "" ? "0" : text}
        </div>
        {past && (
          <p className="text-[15px] font-medium text-warn">
            {t("kiosk.past-max", "More than the {{n}} left: you will be asked why, and a lead's badge if yours cannot allow it.", { n: formatNumber(max) })}
          </p>
        )}
        <div className="grid grid-cols-4 gap-2">
          {[-10, -1, 1, 10].map((delta) => (
            <button key={delta} type="button" onClick={() => add(delta)} className="flex min-h-14 items-center justify-center gap-1 rounded-lg border border-line text-[20px] font-semibold hover:bg-surface-sunk">
              {delta < 0 ? <Minus className="size-4" /> : <Plus className="size-4" />}
              {Math.abs(delta)}
            </button>
          ))}
        </div>
        {max != null && (
          <button
            type="button"
            onClick={() => setText(String(max))}
            className="min-h-14 rounded-lg border-2 border-accent text-[20px] font-semibold text-accent hover:bg-accent-soft"
          >
            {t("kiosk.max", "Max: {{n}}", { n: formatNumber(max) })}
          </button>
        )}
        <div className="grid grid-cols-3 gap-2">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
            <button key={digit} type="button" onClick={() => press(digit)} className="min-h-16 rounded-lg border border-line text-[26px] font-semibold hover:bg-surface-sunk">
              {digit}
            </button>
          ))}
          <button type="button" onClick={() => setText("")} className="min-h-16 rounded-lg border border-line text-[17px] font-semibold hover:bg-surface-sunk">
            {t("kiosk.clear", "Clear")}
          </button>
          <button type="button" onClick={() => press("0")} className="min-h-16 rounded-lg border border-line text-[26px] font-semibold hover:bg-surface-sunk">
            0
          </button>
          <button
            type="button"
            aria-label={t("kiosk.backspace", "Erase")}
            onClick={() => setText((current) => current.slice(0, -1))}
            className="flex min-h-16 items-center justify-center rounded-lg border border-line hover:bg-surface-sunk"
          >
            <Delete className="size-6" />
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export function ScrapDialog({
  title,
  onClose,
  onSubmit,
}: {
  /** Scrapped parts by default; the saw's raw panels say so. */
  title?: string;
  onClose: () => void;
  onSubmit: (quantity: number, reasonId: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const { http } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState<number | null>(null);
  const reasons = useQuery({
    queryKey: ["production", "scrap-reason"],
    queryFn: () => http.get<ScrapReasonDTO[]>("/production/scrap-reason"),
    staleTime: 10 * 60_000,
  });
  const french = i18n.language.startsWith("fr");
  return (
    <Dialog
      open
      onClose={onClose}
      title={title ?? t("kiosk.scrap.title", "Scrapped parts")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={reason == null || quantity <= 0} onClick={() => reason != null && onSubmit(quantity, reason)}>
            {t("kiosk.ok", "OK")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center gap-4">
          <button type="button" aria-label={t("kiosk.less", "One less")} onClick={() => setQuantity(Math.max(1, quantity - 1))} className="flex size-16 items-center justify-center rounded-lg border border-line hover:bg-surface-sunk">
            <Minus className="size-6" />
          </button>
          <span className="w-24 text-center text-[44px] font-bold tabular-nums">{quantity}</span>
          <button type="button" aria-label={t("kiosk.more", "One more")} onClick={() => setQuantity(quantity + 1)} className="flex size-16 items-center justify-center rounded-lg border border-line hover:bg-surface-sunk">
            <Plus className="size-6" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {(reasons.data ?? [])
            .filter((row) => row.is_active)
            .map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setReason(row.id)}
                className={cn(
                  "min-h-14 rounded-lg border-2 px-3 text-[17px] font-medium",
                  reason === row.id ? "border-accent bg-accent-soft" : "border-line hover:bg-surface-sunk",
                )}
              >
                {french ? row.name_fr : row.name}
              </button>
            ))}
        </div>
      </div>
    </Dialog>
  );
}
